import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const SideNavContainer = styled.div`
  width: ${props => props.$collapsed ? '70px' : '250px'};
  height: 100vh;
  background-color: #202023;
  color: white;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  overflow: hidden;
  z-index: 100;
`;

const NavHeader = styled.div`
  padding: ${props => props.$collapsed ? '20px 10px' : '20px'};
  background-color: #202023;
  border-bottom: 1px solid #34495e;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'space-between'};
  min-height: 60px;
`;

const NavTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
  opacity: ${props => props.$collapsed ? 0 : 1};
  width: ${props => props.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  transition: opacity 0.3s ease, width 0.3s ease;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.$collapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLink = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.$collapsed ? '15px 0' : '15px 20px'};
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  cursor: pointer;
  transition: background-color 0.2s, padding 0.3s ease;
  background-color: ${props => props.$active ? '#2a2a2d' : 'transparent'};
  border-left: ${props => props.$active ? '3px solid #e9fe53' : '3px solid transparent'};
  position: relative;
  
  &:hover {
    background-color: #2a2a2d;
  }
`;

const NavIcon = styled.span`
  margin-right: ${props => props.$collapsed ? 0 : '12px'};
  font-size: 18px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavText = styled.span`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  opacity: ${props => props.$collapsed ? 0 : 1};
  width: ${props => props.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  transition: opacity 0.3s ease, width 0.3s ease;
`;

const NavFooter = styled.div`
  padding: ${props => props.$collapsed ? '20px 10px' : '20px'};
  border-top: 1px solid #34495e;
  transition: padding 0.3s ease;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #e9fe53;
  color: black;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #d4e94a;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  margin-left: 10px;
  z-index: 1000;
  
  ${NavLink}:hover & {
    opacity: ${props => props.$collapsed ? 1 : 0};
  }
`;

export function SideNav({ collapsed = false, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    {
      path: '/admin/users',
      icon: <PeopleOutlineIcon fontSize="small" />,
      text: 'User Management'
    },
    {
      path: '/admin/products',
      icon: <InventoryOutlinedIcon fontSize="small" />,
      text: 'Product Catalog'
    },
    {
      path: '/admin/payments',
      icon: <PaymentOutlinedIcon fontSize="small" />,
      text: 'Payment Management'
    },
    {
      path: '/admin/orders',
      icon: <ListAltOutlinedIcon fontSize="small" />,
      text: 'Order Management'
    },
    {
      path: '/admin/invoices/create',
      icon: <ReceiptOutlinedIcon fontSize="small" />,
      text: 'Create Invoice'
    },
    {
      path: '/admin/cron',
      icon: <ScheduleOutlinedIcon fontSize="small" />,
      text: 'Cron Management'
    },
    {
      path: '/admin/google-sheets',
      icon: <TableChartOutlinedIcon fontSize="small" />,
      text: 'Google Sheets'
    },
    {
      path: '/admin/timeouts',
      icon: <TimerOutlinedIcon fontSize="small" />,
      text: 'Timeout Analysis'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin-token');
    navigate('/admin/login');
  };

  return (
    <SideNavContainer $collapsed={collapsed}>
      <NavHeader $collapsed={collapsed}>
        <NavTitle $collapsed={collapsed}>Admin Portal</NavTitle>
        <ToggleButton $collapsed={collapsed} onClick={onToggle} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <ChevronLeftIcon />
        </ToggleButton>
      </NavHeader>
      
      <NavList>
        {navItems.map((item) => (
          <NavItem key={item.path}>
            <NavLink 
              $active={location.pathname === item.path}
              $collapsed={collapsed}
              onClick={() => handleNavigation(item.path)}
            >
              <NavIcon $collapsed={collapsed}>{item.icon}</NavIcon>
              <NavText $collapsed={collapsed}>{item.text}</NavText>
              <Tooltip $collapsed={collapsed}>{item.text}</Tooltip>
            </NavLink>
          </NavItem>
        ))}
      </NavList>
      
      <NavFooter $collapsed={collapsed}>
        <LogoutButton onClick={handleLogout} $collapsed={collapsed}>
          {collapsed ? <LogoutOutlinedIcon fontSize="small" /> : 'Logout'}
        </LogoutButton>
      </NavFooter>
    </SideNavContainer>
  );
} 